import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { IStore } from "@/redux/defaultStore";
import {useGetUserProfile} from "@/hooks/useGetUserProfile";
import { FuelUpApi, FuelUpMoveRequest, Token } from "@devour/client";
import getConfig from "@/utils/getConfig";

// Types for the FuelUp game state API response
export interface FuelUpGameState {
    id: string;
    userId: string;
    currentPosition: number;
    totalMovesCompleted: number;
    availableMoves: number;
    spendRemainder: number;
    totalFuelSpent: number;
    totalFuelEarned: number;
    prestigeLevel: number;
    completedLoops: number;
    lastMoveAt?: string;
    isActive: boolean;
    unlockedAchievements: string[];
    longestStreak: number;
    bestSingleMove: number;
    lastLoginBonus?: string;
    claimedSpaces: Array<{ loop: number; space: number }>;
    spunWheels: Array<{ loop: number; position: number; spinWheelConfigId: string; spunAt?: string }>;
    createdAt: number;
    updatedAt: number;
}

export interface FuelUpVaultState {
    id: string;
    userId: string;
    level: number;
    storedFuel: number;
    maxCapacity: number;
    evolutionTier: string;
    lastDepositAt?: string;
    lastWithdrawAt?: string;
    nextEvolutionAt?: string;
    lastBonusAt?: string;
    totalBonusEarned: number;
    isActive: boolean;
    evolutionProgress: number;
    totalEvolutions: number;
    totalDeposited: number;
    totalWithdrawn: number;
    longestHoldStreak: number;
    currentBonusRate: number;
    canEvolveNow: boolean;
    timeToNextEvolution?: number;
    /** Transaction history entries — shape varies by backend version, typed loosely */
    recentTransactions: any[];
    currentVaultBonusTier?: any;
    lastDailyBonus: number;
    lastWeeklyBonus: number;
    lastMonthlyBonus: number;
    lifetimeBonusTotal: number;
    fuelPricePerUsd: number;
    vaultStartedAt?: string;
    createdAt: number;
    updatedAt: number;
}

export interface FuelUpPrestigeState {
    id: string;
    userId: string;
    currentLevel: number;
    totalPrestigePoints: number;
    lifetimePrestigeCount: number;
    lastPrestigeAt?: string;
    nextPrestigeCost: number;
    /** Active prestige bonuses — shape varies by backend version, typed loosely */
    activeBonuses: any[];
    totalFuelSacrificed: number;
    highestPrestigeLevel: number;
    currentPrestigeStreak: number;
    firstPrestigeAt?: string;
    unlockedPrestigeAchievements: string[];
    canPrestigeNow: boolean;
    availableBonuses: any[];
    recentPrestigeHistory: any[];
    totalBonusMultipliers: {
        fuelMultiplier: number;
        vaultCapacity: number;
        bonusBonusRate: number;
        moveDiscount: number;
    };
    createdAt: number;
    updatedAt: number;
}

export interface VaultBonusTierSummary {
    tierName: string;
    tierDescription?: string;
    daysHeldMin: number;
    daysHeldMax: number;
    minVaulted: number;
    maxVaulted?: number;
    dailyFuelBonusPercent: number;
    weeklyFuelBonusPercent: number;
    monthlyFuelBonusPercent: number;
}

export interface FuelUpGameStateResponse {
    gameState: FuelUpGameState;
    vault?: FuelUpVaultState;
    prestige?: FuelUpPrestigeState;
    dollarsPerMove: number;
    allVaultBonusTiers: VaultBonusTierSummary[];
}

/**
 * Query key factory for FuelUp queries.
 * Exported for use by external invalidation callers (e.g., after WebSocket events).
 */
export const fuelUpQueryKeys = {
    all: ["fuelup"] as const,
    gameState: (userId: string) => [...fuelUpQueryKeys.all, "game-state", userId] as const,
    activeBoard: (loopLevel: number) => [...fuelUpQueryKeys.all, "active-board", loopLevel] as const,
};

// Fetch function for game state
async function fetchFuelUpGameState(userId: string, fullToken: Token): Promise<FuelUpGameStateResponse> {
    const data = await new FuelUpApi(getConfig(fullToken)).getFuelupGameStateId({id: userId});
    return data as unknown as FuelUpGameStateResponse;
}

/**
 * Fetches the current user's FuelUp game state (board position, vault, prestige).
 * Polls with a 30-second staleTime — kept short because moves update position immediately.
 */
export function useFuelUpGameState() {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const {data: currentUser} = useGetUserProfile(fullToken);

    const userId = currentUser?.user?.id;
    const token = fullToken?.token;
    const enabled = Boolean(userId && token);

    return useQuery({
        queryKey: fuelUpQueryKeys.gameState(userId ?? ""),
        queryFn: () => fetchFuelUpGameState(userId!, fullToken!),
        enabled,
        staleTime: 30 * 1000, // 30 seconds - balance between freshness and API calls
        refetchOnWindowFocus: false,
    });
}

/**
 * Returns a function that invalidates the FuelUp game state cache.
 * Call after any mutation that modifies game state (move, claim, vault deposit/withdraw).
 */
export function useInvalidateFuelUpGameState() {
    const queryClient = useQueryClient();
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const {data: currentUser} = useGetUserProfile(fullToken);
    const userId = currentUser?.user?.id;

    return () => {
        if (userId) {
            queryClient.invalidateQueries({ queryKey: fuelUpQueryKeys.gameState(userId) });
        }
    };
}

/**
 * Mutation hook to make a FuelUp board move.
 * Backend derives the loop level from stored game state — the client never sends loopLevel.
 *
 * @param fuelUpMoveRequest - Move details (moveType, spaceAction)
 */
export function useFuelUpMove() {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const {data: currentUser} = useGetUserProfile(fullToken);
    const queryClient = useQueryClient();

    const userId = currentUser?.user?.id;
    const token = fullToken?.token;

    return useMutation({
        mutationFn: async (fuelUpMoveRequest: FuelUpMoveRequest) => {
            if (!userId || !token || !fullToken) throw new Error("Not authenticated");
            const api = new FuelUpApi(getConfig(fullToken));
            return api.createFuelupGameStateIdMove({ id: userId, fuelUpMoveRequest }) as unknown as Record<string, unknown>;
        },
        onSuccess: () => {
            // Invalidate game state so both main game and mini tracker update
            if (userId) {
                queryClient.invalidateQueries({ queryKey: fuelUpQueryKeys.gameState(userId) });
            }
        },
    });
}

/**
 * Mutation hook to claim the reward for the current board space.
 * Backend reads pending reward IDs from stored game state — no params required.
 */
export function useFuelUpClaimReward() {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const {data: currentUser} = useGetUserProfile(fullToken);
    const queryClient = useQueryClient();

    const userId = currentUser?.user?.id;
    const token = fullToken?.token;

    return useMutation({
        mutationFn: async () => {
            if (!userId || !token || !fullToken) throw new Error("Not authenticated");
            const api = new FuelUpApi(getConfig(fullToken));
            return api.createFuelupGameStateIdClaimReward({ id: userId }) as unknown as Record<string, unknown>;
        },
        onSuccess: () => {
            if (userId) {
                queryClient.invalidateQueries({ queryKey: fuelUpQueryKeys.gameState(userId) });
            }
        },
    });
}

/**
 * Mutation hook for vault deposit or withdrawal.
 *
 * @param action - "deposit" or "withdraw"
 * @param amount - Amount of Fuel to deposit/withdraw
 */
export function useFuelUpVaultAction() {
    const fullToken = useSelector((store: IStore) => store.authStore.fullToken);
    const {data: currentUser} = useGetUserProfile(fullToken);
    const queryClient = useQueryClient();

    const userId = currentUser?.user?.id;
    const token = fullToken?.token;

    return useMutation({
        mutationFn: async ({ action, amount }: { action: "deposit" | "withdraw"; amount: number }) => {
            if (!userId || !fullToken) throw new Error("Not authenticated");

            const data = await new FuelUpApi(getConfig(fullToken)).createFuelupVaultIdAction({
                id: userId,
                // `action as any` required: generated enum type doesn't match the string literal union
                fuelUpVaultActionRequest: {action: action as any, amount},
            });
            return data as unknown as Record<string, unknown>;
        },
        onSuccess: () => {
            if (userId) {
                queryClient.invalidateQueries({ queryKey: fuelUpQueryKeys.gameState(userId) });
            }
        },
    });
}
